import React, { useState, useEffect, useRef } from 'react'
import { m } from 'framer-motion'
import { CalendarCheck, Menu, PhoneCall, X, Sun, Moon, Globe } from 'lucide-react'
import { useTheme } from '../../../context/ThemeContext'
import { useLanguage } from '../../../context/LanguageContext'
import { useRouter } from '../../../router/Router'
import { PHONE_LINK, NAVER_BOOKING_URL } from '../../../constants/contact'
import { trackCtaClick, trackEvent } from '../../../utils/analytics'

const Navigation: React.FC = () => {
    const sectionIds = ['hero', 'intro', 'menu-detail', 'gallery', 'reviews', 'press', 'contact']

    const [isScrolled, setIsScrolled] = useState(false)
    const [isHidden, setIsHidden] = useState(false)
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
    const [activeSectionId, setActiveSectionId] = useState<string>('hero')
    const rafIdRef = useRef<number | null>(null)
    const lastScrollYRef = useRef(0)
    const { theme, toggleTheme } = useTheme()
    const { language, setLanguage, t } = useLanguage()
    const { navigate, path } = useRouter()

    /* 스크롤 스파이: 현재 보이는 섹션으로 네비 하이라이트 → 전체 구조가 한눈에 */
    useEffect(() => {
        const observers: IntersectionObserver[] = []
        const options: IntersectionObserverInit = {
            root: null,
            rootMargin: '-20% 0px -60% 0px',
            threshold: 0,
        }
        sectionIds.forEach((id) => {
            const el = document.getElementById(id)
            if (!el) return
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) setActiveSectionId(id)
                    })
                },
                options
            )
            observer.observe(el)
            observers.push(observer)
        })
        return () => observers.forEach((o) => o.disconnect())
    }, [])

    /* 스크롤 방향에 따라 헤더 숨김/노출 → 아래로 읽을 땐 헤더가 사라져 콘텐츠 몰입,
       위로 올리면 다시 노출. 상단 근처와 모바일 메뉴 열림 시에는 항상 노출. */
    useEffect(() => {
        const HIDE_THRESHOLD = 120 // 이 지점 아래에서만 숨김 동작
        const DELTA = 6            // 미세 흔들림 무시
        const handleScroll = () => {
            if (rafIdRef.current != null) return
            rafIdRef.current = requestAnimationFrame(() => {
                const y = window.scrollY
                setIsScrolled(y > 50)

                const last = lastScrollYRef.current
                if (Math.abs(y - last) > DELTA) {
                    if (y > last && y > HIDE_THRESHOLD) {
                        setIsHidden(true)   // 아래로 스크롤 → 숨김
                    } else if (y < last) {
                        setIsHidden(false)  // 위로 스크롤 → 노출
                    }
                    lastScrollYRef.current = y
                }
                rafIdRef.current = null
            })
        }
        window.addEventListener('scroll', handleScroll, { passive: true })
        return () => {
            window.removeEventListener('scroll', handleScroll)
            if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current)
        }
    }, [])

    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = ''
        }
        return () => {
            document.body.style.overflow = ''
        }
    }, [isMobileMenuOpen])

    const scrollToSection = (sectionId: string) => {
        const element = document.getElementById(sectionId)
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' })
        } else {
            // 홈이 아닌 페이지(예: /story)에서는 홈으로 이동 후 해당 섹션으로
            navigate(`/#${sectionId}`)
        }
        setIsMobileMenuOpen(false)
    }

    type NavItem = { id: string; label: string; route?: string }
    const menuItems: NavItem[] = [
        { id: 'story', label: t('nav.storyPage'), route: '/story' },
        { id: 'menu-detail', label: t('nav.menu') },
        { id: 'gallery', label: t('nav.gallery') },
        { id: 'reviews', label: t('nav.reviews') },
        // 언론보도는 한국어에서만 노출
        ...(language === 'ko' ? [{ id: 'press', label: t('nav.press') }] : []),
        { id: 'location', label: t('nav.location'), route: '/location' },
        { id: 'contact', label: t('nav.contact') },
    ]

    const handleNavClick = (item: NavItem) => {
        trackEvent('nav_click', { section: item.id })
        if (item.route) {
            navigate(item.route)
            setIsMobileMenuOpen(false)
        } else {
            scrollToSection(item.id)
        }
    }

    const isNavActive = (item: NavItem): boolean =>
        item.route ? path === item.route : path === '/' && activeSectionId === item.id

    const toggleLanguage = () => {
        setLanguage(language === 'ko' ? 'en' : 'ko')
    }

    return (
        <>
            <m.nav
                initial={{ y: -100 }}
                animate={{ y: isHidden && !isMobileMenuOpen ? '-100%' : 0 }}
                transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                className={`restaurant-navigation ${isScrolled ? 'scrolled' : ''} theme-${theme.mode}`}
            >
                <div className="restaurant-nav-container">
                    {/* Logo */}
                    <button
                        onClick={() => scrollToSection('hero')}
                        className="restaurant-logo"
                        aria-label="홈으로 가기"
                    >
                        <span className="restaurant-logo-black">BLACKPORK</span>
                        <span className="restaurant-logo-red ml-2">1931</span>
                    </button>

                    {/* Desktop Menu - 데스크톱(≥1025px)에서는 섹션 링크를 숨기고 사이드 내비로 대체 */}
                    <div className="restaurant-nav-menu">
                        <div className="restaurant-nav-links">
                            {menuItems.map((item) => (
                                <m.button
                                    key={item.id}
                                    onClick={() => handleNavClick(item)}
                                    className={`restaurant-nav-item ${isNavActive(item) ? 'restaurant-nav-item-active' : ''}`}
                                    whileHover={{ y: -2 }}
                                >
                                    {item.label}
                                </m.button>
                            ))}
                        </div>

                        {/* 이야기(/story) 페이지 링크 — 데스크톱에서 섹션 링크가 숨겨져도 항상 노출 */}
                        <button
                            onClick={() => navigate('/story')}
                            className={`restaurant-nav-item restaurant-nav-story-link ${path === '/story' ? 'restaurant-nav-item-active' : ''}`}
                        >
                            {t('nav.storyPage')}
                        </button>

                        {/* Language Toggle Button */}
                        <div className="restaurant-language-toggle">
                            <m.button
                                onClick={() => setLanguage('ko')}
                                className={`restaurant-language-option ${language === 'ko' ? 'active' : ''}`}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                aria-label="한국어"
                            >
                                <Globe size={12} />
                                <span className="restaurant-language-text">KO</span>
                            </m.button>
                            <m.button
                                onClick={() => setLanguage('en')}
                                className={`restaurant-language-option ${language === 'en' ? 'active' : ''}`}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                aria-label="English"
                            >
                                <Globe size={12} />
                                <span className="restaurant-language-text">EN</span>
                            </m.button>
                        </div>

                        {/* Theme Toggle Button */}
                        <m.button
                            onClick={toggleTheme}
                            className="restaurant-theme-toggle"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            aria-label={theme.mode === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}
                        >
                            {theme.mode === 'dark' ? (
                                <Sun size={20} />
                            ) : (
                                <Moon size={20} />
                            )}
                        </m.button>

                        <div className="restaurant-nav-cta">
                            <a
                                href={PHONE_LINK}
                                className="restaurant-nav-cta-button restaurant-nav-cta-phone"
                                aria-label={t('cta.phoneAria')}
                                onClick={() => trackCtaClick('call', 'header')}
                            >
                                <PhoneCall size={16} />
                                <span>{t('cta.phone')}</span>
                            </a>
                            <a
                                href={NAVER_BOOKING_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="restaurant-nav-cta-button restaurant-nav-cta-reserve"
                                aria-label={t('cta.reserveAria')}
                                onClick={() => trackCtaClick('reserve', 'header')}
                            >
                                <CalendarCheck size={16} />
                                <span>{t('cta.reserve')}</span>
                            </a>
                        </div>
                    </div>

                    {/* Mobile Menu Button - 모바일에서만 표시 */}
                    <div className="restaurant-mobile-controls">
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="restaurant-mobile-menu-button"
                            aria-label="메뉴"
                            aria-expanded={isMobileMenuOpen}
                        >
                            <Menu size={24} />
                        </button>
                    </div>
                </div>
            </m.nav>

            {/* Mobile Menu Overlay */}
            {isMobileMenuOpen && (
                <m.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`restaurant-mobile-overlay theme-${theme.mode}`}
                />
            )}

            {/* Mobile Menu - 하단 바텀시트 */}
            <m.div
                initial={{ y: '100%' }}
                animate={{
                    y: isMobileMenuOpen ? 0 : '100%',
                }}
                transition={{ type: 'spring', stiffness: 320, damping: 34 }}
                className={`restaurant-mobile-menu theme-${theme.mode}`}
                role="dialog"
                aria-modal="true"
            >
                <div className="restaurant-mobile-menu-inner">
                    {/* 바텀시트 드래그 핸들 + 닫기 */}
                    <div className="restaurant-mobile-menu-header">
                        <span className="restaurant-mobile-sheet-handle" aria-hidden="true" />
                        <button
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="restaurant-mobile-close-button"
                            aria-label="메뉴 닫기"
                        >
                            <X size={22} />
                        </button>
                    </div>

                    {/* Menu Items - 현재 섹션 표시 */}
                    <div className="restaurant-mobile-menu-content flex flex-col p-6 space-y-2">
                        {menuItems.map((item, index) => (
                            <m.button
                                key={item.id}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                onClick={() => handleNavClick(item)}
                                className={`restaurant-mobile-nav-item ${isNavActive(item) ? 'restaurant-mobile-nav-item-active' : ''}`}
                            >
                                {item.label}
                            </m.button>
                        ))}

                        {/* Mobile Language Toggle */}
                        <div className="restaurant-mobile-language-toggle">
                            <m.button
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: menuItems.length * 0.1 }}
                                onClick={() => setLanguage('ko')}
                                className={`restaurant-mobile-language-option ${language === 'ko' ? 'active' : ''}`}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                aria-label="한국어"
                            >
                                <Globe size={16} />
                                <span className="restaurant-mobile-language-text">KO</span>
                            </m.button>
                            <m.button
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: menuItems.length * 0.1 }}
                                onClick={() => setLanguage('en')}
                                className={`restaurant-mobile-language-option ${language === 'en' ? 'active' : ''}`}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                aria-label="English"
                            >
                                <Globe size={16} />
                                <span className="restaurant-mobile-language-text">EN</span>
                            </m.button>
                        </div>

                        {/* Mobile Theme Toggle */}
                        <m.button
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: (menuItems.length + 1) * 0.1 }}
                            onClick={toggleTheme}
                            className="restaurant-mobile-nav-item"
                        >
                            {theme.mode === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}
                        </m.button>

                        <div className="restaurant-mobile-quick-actions">
                            <a
                                href={PHONE_LINK}
                                className="restaurant-mobile-quick-action restaurant-mobile-quick-phone"
                                aria-label={t('cta.phoneAria')}
                                onClick={() => trackCtaClick('call', 'header')}
                            >
                                <PhoneCall size={18} />
                                <span>{t('cta.phoneFull')}</span>
                            </a>
                            <a
                                href={NAVER_BOOKING_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="restaurant-mobile-quick-action restaurant-mobile-quick-reserve"
                                aria-label={t('cta.reserveAria')}
                                onClick={() => trackCtaClick('reserve', 'header')}
                            >
                                <CalendarCheck size={18} />
                                <span>{t('cta.reserveFull')}</span>
                            </a>
                        </div>
                    </div>
                </div>
            </m.div>
        </>
    )
}

export default Navigation
